import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { 
  themeAtom, 
  resolvedThemeAtom, 
  systemThemeAtom, 
  setThemeAtom,
  type Theme,
  type ResolvedTheme 
} from '../store/theme';

export interface UseThemeReturn {
  /** Current theme setting (light/dark/system) */
  theme: Theme;
  /** Resolved theme (light/dark) - what's actually applied */
  resolvedTheme: ResolvedTheme;
  /** Current system preference (light/dark) */
  systemTheme: ResolvedTheme;
  /** Function to change the theme */
  setTheme: (theme: Theme) => void;
  /** Whether the current resolved theme is dark */
  isDark: boolean;
  /** Whether the current resolved theme is light */
  isLight: boolean;
  /** Whether the theme is set to follow system preference */
  isSystem: boolean;
}

export const useTheme = (): UseThemeReturn => {
  const theme = useAtomValue(themeAtom);
  const resolvedTheme = useAtomValue(resolvedThemeAtom);
  const [systemTheme, setSystemTheme] = useAtom(systemThemeAtom);
  const setTheme = useSetAtom(setThemeAtom);

  // Listen for system theme changes
  useEffect(() => {
    const handleSystemThemeChange = (event: CustomEvent<{ theme: ResolvedTheme }>) => {
      setSystemTheme(event.detail.theme);
    };

    // Type assertion for the custom event
    const typedHandler = handleSystemThemeChange as EventListener;
    
    window.addEventListener('system-theme-change', typedHandler);
    
    return () => {
      window.removeEventListener('system-theme-change', typedHandler);
    };
  }, [setSystemTheme]);

  // Update resolved theme when system theme changes and current theme is 'system'
  useEffect(() => {
    if (theme === 'system') {
      // Re-trigger theme application when system theme changes
      setTheme('system');
    }
  }, [systemTheme, theme, setTheme]);

  return {
    theme,
    resolvedTheme,
    systemTheme,
    setTheme,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
    isSystem: theme === 'system',
  };
}; 